import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useJoinMeetingInvite } from '@/hooks/useJoinMeetingInvite'
import { useMeetingInvites } from '@/hooks/useMeetingInvites'

/**
 * Surfaces live meeting invites pushed over the socket (BRAI-533) and offers
 * the one action worth taking while the sharer is still recording: join via Yes.
 *
 * What Yes does, and why the room is re-checked before it does it, lives in
 * `useJoinMeetingInvite` — the Ongoing Meetings row on Home offers the same
 * action and must not reach a different conclusion about it. What belongs to
 * this screen is the navigation: the create form lives in `AppLayout`, which
 * the live screen is outside of.
 *
 * Centered modal (blocking): the user must choose Yes or No — backdrop and
 * Escape do not dismiss. Only one invite is shown at a time (newest first).
 * Layout follows Figma Brse.AI node 627-8671 (modal 627-8808).
 */
export default function MeetingInviteBanner() {
  const { t } = useUiStrings()
  const navigate = useNavigate()
  const { invites, dismiss, confirmStillLive } = useMeetingInvites()
  const { join, checkingId, endedIds, clearEnded } = useJoinMeetingInvite(confirmStillLive)

  const invite = invites[0] ?? null
  const hasEnded = invite ? endedIds.includes(invite.meetingId) : false
  const isChecking = invite ? checkingId === invite.meetingId : false
  const modalPanelRef = useRef<HTMLDivElement>(null)
  const inviteSharedAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!invite) {
      inviteSharedAtRef.current = null
      return
    }
    if (
      inviteSharedAtRef.current !== null &&
      inviteSharedAtRef.current !== invite.sharedAt
    ) {
      clearEnded(invite.meetingId)
    }
    inviteSharedAtRef.current = invite.sharedAt
  }, [invite, clearEnded])

  useEffect(() => {
    modalPanelRef.current?.focus()
  }, [invite?.meetingId])

  const dismissInvite = useCallback(() => {
    if (!invite) return
    clearEnded(invite.meetingId)
    dismiss(invite.meetingId)
  }, [clearEnded, dismiss, invite])

  if (!invite) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="meeting-invite-title"
      data-testid="meeting-invite-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-[rgba(15,23,42,0.35)] px-4 py-4 backdrop-blur-[2px]"
    >
      <div
        ref={modalPanelRef}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
        className="animate-scale-in relative w-full max-w-[490px] rounded-2xl border border-slate-100 bg-[var(--bg-secondary)] p-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] outline-none dark:border-[var(--border-color)]"
      >
        <div className="flex items-center gap-3.5 pb-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#eff6ff] text-blue-600 dark:bg-[var(--accent-light)] dark:text-[var(--accent)]">
            <MeetingInviteIcon className="h-5 w-5" />
          </div>
          <h2
            id="meeting-invite-title"
            className="text-base font-bold leading-[22px] text-[var(--text-primary)]"
          >
            {t.meetingInviteTitle}
          </h2>
        </div>

        {hasEnded ? (
          <p className="text-xs leading-[18px] text-[var(--text-secondary)]">{t.meetingInviteEnded}</p>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-xs leading-[18px] text-[var(--text-secondary)]">
              <span className="font-bold text-[var(--text-primary)]">{invite.sharedBy.name}</span>
              {t.meetingInviteModalAfterName}
              <span className="font-bold text-[var(--text-primary)]">{invite.title}</span>
              {t.meetingInviteModalAfterMeeting}
            </p>

            <div className="flex gap-2.5 rounded-xl border border-[#dbeafe] bg-[#f0f6fe] p-3.5 dark:border-sky-900/40 dark:bg-sky-950/40">
              <InviteInfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-sky-200" />
              <p className="text-[11px] leading-[1.62] text-slate-600 dark:text-sky-100">
                {t.sharedRoomDetectionInfo}
              </p>
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2.5">
          {hasEnded ? (
            <button
              type="button"
              onClick={dismissInvite}
              className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-2 text-xs font-bold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
            >
              {t.meetingInviteNo}
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isChecking}
                onClick={dismissInvite}
                className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-2 text-xs font-bold text-[var(--text-primary)] shadow-sm transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
              >
                {t.meetingInviteNo}
              </button>
              <button
                type="button"
                disabled={isChecking}
                onClick={() =>
                  void join(invite, () => {
                    dismissInvite()
                    navigate('/home')
                  })
                }
                className="btn-primary rounded-lg px-5 py-2 text-xs font-bold shadow-sm disabled:opacity-50"
              >
                {isChecking ? t.meetingInviteChecking : t.meetingInviteYes}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function MeetingInviteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 11c1.657 0 3-1.567 3-3.5S17.657 4 16 4s-3 1.567-3 3.5 1.343 3.5 3 3.5zM8 11c1.657 0 3-1.567 3-3.5S9.657 4 8 4 5 5.567 5 7.5 7.343 11 8 11zM8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zM16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
        fill="currentColor"
      />
    </svg>
  )
}

function InviteInfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-10a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 018 5zm0 8a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  )
}
